import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Lock, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { ApiError } from '@/lib/api'
import { Button, Spinner } from '@/components/ui'
import { KaproLogo } from '@/components/Logo'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

/**
 * Where a scanned worker badge lands.
 *
 * The card's QR encodes `{FRONTEND_URL}/prijava/kartica/{token}`, a full URL
 * rather than a bare token, so the phone's own camera app opens the app
 * directly — the worker never has to find and launch it first. That is the
 * whole point of the badge: one action, standing at a machine, with gloves on.
 *
 * The token is consumed immediately and the URL replaced, so it does not sit in
 * the address bar or the history of a shared shop-floor device.
 */
export function BadgeLoginPage() {
  const { token } = useParams<{ token: string }>()
  const { user, loginWithBadge, isLoading } = useAuth()
  const navigate = useNavigate()
  const t = useT()

  const [error, setError] = useState<string | null>(null)

  /*
   * The card was fine and the door is shut — the company is suspended, or no
   * longer allows signing in by card. The server's sentence is shown as sent;
   * the lost-card advice below it is not, because the card is not the
   * problem. Same treatment as the sign-in form's.
   */
  const [refused, setRefused] = useState(false)

  /*
   * StrictMode mounts effects twice in development, and this one spends a
   * single-use credential. Without the guard the second run races the first and
   * one of them reports a failure for a sign-in that actually worked.
   */
  const attempted = useRef(false)

  useEffect(() => {
    if (!token || attempted.current || isLoading || user) return
    attempted.current = true

    loginWithBadge(token)
      .then((pending) => {
        if (pending) {
          // The account carries a second factor; the badge alone is not enough.
          navigate(`/prijava?izazov=${encodeURIComponent(pending.challenge)}`, { replace: true })
          return
        }

        navigate('/', { replace: true })
      })
      .catch((err) => {
        setRefused(err instanceof ApiError && err.status === 403)
        setError(
          err instanceof ApiError
            ? (err.fieldError('token') ?? err.message)
            : t('profile.badgeLogin.failed'),
        )
      })
    // `t` changes when the language does; the `attempted` guard above is what
    // keeps that from spending a second single-use token.
  }, [token, user, isLoading, loginWithBadge, navigate, t])

  if (user) return <Navigate to="/" replace />

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-6">
      <KaproLogo size="lg" />

      {!error ? (
        <>
          <Spinner label={t('profile.badgeLogin.working')} />
          <p className="muted max-w-xs text-center text-sm">
            <ShieldCheck className="mr-1 inline size-4" aria-hidden />
            {t('profile.badgeLogin.recognising')}
          </p>
        </>
      ) : (
        <div className="w-full max-w-sm space-y-4 text-center">
          <p
            role="alert"
            className={cn(
              'flex items-start gap-2 rounded-xl px-3 py-2.5 text-left text-sm',
              refused
                ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/60 dark:text-amber-100'
                : 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
            )}
          >
            {refused && <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />}
            <span>{error}</span>
          </p>

          {!refused && <p className="muted text-sm">{t('profile.badgeLogin.lostCard')}</p>}

          <Button fullWidth onClick={() => navigate('/prijava', { replace: true })}>
            {t('profile.badgeLogin.usePassword')}
          </Button>
        </div>
      )}
    </div>
  )
}
